import { FC } from 'react';
import { useTranslations } from 'next-intl';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Logo, Button, Container } from '@components/design-system';
import { NotificationsMenu } from '@components/dropdown/notifications-menu';
import { UserMenu, UserProfile } from '@components/dropdown/user-menu';
import { MOBILE_OFFSET } from '@constants';
import { login } from '@lib/beacon';
import { actions } from '@state/auth';
import { actions as globalActions } from '@state/global';
import { RootState } from '@state/store';
import * as Styles from './styles';

const Search = dynamic(() => import('@components/popovers/search'), { ssr: false });

export const Header: FC = () => {
  const t = useTranslations('header');
  const router = useRouter();
  const dispatch = useDispatch();
  const auth = useSelector((state: RootState) => state.auth);
  const address = auth.account?.address;

  const handleLogin = async () => {
    const account = await login();
    dispatch({ type: actions.AUTH_LOGIN, payload: account });
  };

  const handleShowProfile = async () => {
    dispatch({ type: globalActions.GLOBAL_MOBILE_OFFSET, payload: MOBILE_OFFSET.LEFT });
  };

  const handleShowMenu = async () => {
    dispatch({ type: globalActions.GLOBAL_MOBILE_OFFSET, payload: MOBILE_OFFSET.RIGHT });
  };

  return (
    <>
      <Styles.Container>
        <Container css={{ width: '100%', height: '$space$1' }}>
          {/* MOBILE */}
          <Styles.Mobile
            css={{
              position: 'relative',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Button as="a" variant="basic">
              <Box vertical>
                {address ? (
                  <Button variant="basic" onClick={handleShowProfile}>
                    <UserProfile />
                  </Button>
                ) : (
                  <Button variant="secondary" onClick={handleLogin}>
                    {t('login')}
                  </Button>
                )}
              </Box>
            </Button>

            <Link href="/" locale={router.locale} prefetch={false} passHref>
              <Box
                as="a"
                aria-label="Go to Homepage"
                css={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}
              >
                <Logo />
              </Box>
            </Link>

            <Button as="a" variant="basic" onClick={handleShowMenu}>
              <Box vertical>
                <i>menu</i>
              </Box>
            </Button>
          </Styles.Mobile>

          {/* DESKTOP */}
          <Styles.Desktop
            css={{
              position: 'relative',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Box vertical>
              <Link href="/" locale={router.locale} prefetch={false} passHref>
                <a aria-label="Go to Homepage">
                  <Logo />
                </a>
              </Link>
            </Box>

            <Box css={{ flex: 'auto', paddingLeft: '$space$3' }}>
              <Search />
            </Box>

            <Styles.Auth>
              {address && (
                <Link href="/mint" locale={router.locale} prefetch={false} passHref>
                  <Button as="a" variant="basic">
                    {t('mint')}
                  </Button>
                </Link>
              )}

              <Link href="/boards" locale={router.locale} prefetch={false} passHref>
                <Button as="a" variant="basic">
                  {t('boards')}
                </Button>
              </Link>

              <Link href="/collections" locale={router.locale} prefetch={false} passHref>
                <Button as="a" variant="basic">
                  {t('collections')}
                </Button>
              </Link>

              <Link href="/explore" locale={router.locale} prefetch={false} passHref>
                <Button as="a" variant="basic">
                  {t('explore')}
                </Button>
              </Link>

              {address ? (
                <>
                  <NotificationsMenu />
                  <UserMenu />
                </>
              ) : (
                <Button variant="secondary" onClick={handleLogin}>
                  {t('login')}
                </Button>
              )}
            </Styles.Auth>
          </Styles.Desktop>
        </Container>
      </Styles.Container>

      <Styles.Mobile>
        <Container css={{ width: '100%' }}>
          <Box css={{ height: 64 }} vertical>
            <Search isMobile />
          </Box>
        </Container>
      </Styles.Mobile>
    </>
  );
};
